import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Box, Loader2 } from "lucide-react";
import { useProductSearch } from "../hooks/useProductSearch";
import { formatPrice } from "../lib/api";

const VARIANT_CLASS = {
    header: "flex w-full min-w-0",
    catalog: "flex w-full min-w-0",
    admin: "flex w-full min-w-0",
    pos: "flex w-full min-w-0",
};

export default function ProductSearchBox({
    variant = "catalog",
    showStock = false,
    limit = 12,
    placeholder = "Buscar producto, código, marca…",
    value: controlledValue,
    onQueryChange,
    onSelect,
    syncUrlOnCatalog = false,
    className = "",
    inputTestId,
}) {
    const navigate = useNavigate();
    const wrapRef = useRef(null);
    const inputRef = useRef(null);
    const { query, setQuery, results, total, loading } = useProductSearch({
        limit,
        externalQuery: controlledValue !== undefined ? controlledValue : undefined,
    });
    const [open, setOpen] = useState(false);
    const [activeIdx, setActiveIdx] = useState(-1);

    const displayQuery = controlledValue !== undefined ? controlledValue : query;

    const setDisplayQuery = (v) => {
        if (controlledValue === undefined) setQuery(v);
        onQueryChange?.(v);
    };

    const showDropdown = open && displayQuery.trim().length > 0;

    useEffect(() => {
        const onDoc = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const pickProduct = (p) => {
        setOpen(false);
        setDisplayQuery("");
        if (onSelect) {
            onSelect(p);
            return;
        }
        navigate(`/producto/${p.id}`);
    };

    const onKeyDown = (e) => {
        if (!showDropdown) {
            if (e.key === "Enter" && displayQuery.trim()) {
                e.preventDefault();
                navigate(`/productos?q=${encodeURIComponent(displayQuery.trim())}`);
            }
            return;
        }
        if (e.key === "Escape") {
            setOpen(false);
            return;
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIdx((i) => Math.min(i + 1, results.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIdx((i) => Math.max(i - 1, -1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (activeIdx >= 0 && results[activeIdx]) pickProduct(results[activeIdx]);
            else if (displayQuery.trim()) navigate(`/productos?q=${encodeURIComponent(displayQuery.trim())}`);
        }
    };

    const inputCls =
        variant === "header"
            ? "flex-1 px-4 py-2.5 bg-white text-[var(--lz-navy)] text-sm focus:outline-none border-2 border-white"
            : "flex-1 px-4 py-2 text-sm focus:outline-none border-2 border-[var(--lz-navy)] bg-white";

    const btnCls =
        variant === "header"
            ? "px-5 bg-[var(--lz-orange)] text-[var(--lz-navy)] border-2 border-[var(--lz-orange)] hover:bg-white shrink-0"
            : "px-4 bg-[var(--lz-orange)] text-[var(--lz-navy)] border-2 border-[var(--lz-navy)] shrink-0 hover:bg-[var(--lz-navy)] hover:text-white";

    return (
        <div ref={wrapRef} className={`relative ${className}`}>
            <div className={VARIANT_CLASS[variant] || VARIANT_CLASS.catalog}>
                <input
                    ref={inputRef}
                    type="text"
                    value={displayQuery}
                    onChange={(e) => {
                        setDisplayQuery(e.target.value);
                        setOpen(true);
                        setActiveIdx(-1);
                    }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKeyDown}
                    placeholder={placeholder}
                    className={inputCls}
                    data-testid={inputTestId || `search-input-${variant}`}
                    autoComplete="off"
                />
                <button
                    type="button"
                    onClick={() => {
                        if (displayQuery.trim()) {
                            navigate(`/productos?q=${encodeURIComponent(displayQuery.trim())}`);
                        }
                    }}
                    className={btnCls}
                    aria-label="Buscar"
                >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </button>
            </div>

            {showDropdown && (
                <div
                    className={`absolute left-0 right-0 top-full z-50 mt-1 border-2 border-[var(--lz-navy)] bg-white shadow-[4px_4px_0_#0f1419] max-h-96 overflow-auto ${
                        variant === "admin" || variant === "pos" ? "min-w-[min(100%,32rem)]" : ""
                    }`}
                    data-testid={`search-dropdown-${variant}`}
                >
                    {loading && results.length === 0 && (
                        <div className="p-4 text-xs text-[var(--lz-navy)]/60 font-mono">Buscando…</div>
                    )}
                    {!loading && results.length === 0 && (
                        <div className="p-4 text-xs text-[var(--lz-navy)]/60">Sin coincidencias</div>
                    )}
                    {results.map((p, i) => (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => pickProduct(p)}
                            className={`w-full flex items-center gap-3 p-3 text-left border-b border-[var(--lz-navy)]/10 hover:bg-[var(--lz-orange)]/15 ${
                                activeIdx === i ? "bg-[var(--lz-orange)]/25" : ""
                            }`}
                        >
                            <div className="w-10 h-10 shrink-0 bg-[#f3f4f6] border border-[var(--lz-navy)]/20 flex items-center justify-center overflow-hidden">
                                {p.images?.[0] ? (
                                    <img src={p.images[0]} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <Box className="w-5 h-5 text-[var(--lz-navy)]/30" />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-sm uppercase truncate">{p.name}</div>
                                <div className="text-[10px] font-mono text-[var(--lz-navy)]/60">
                                    {p.sku && `SKU ${p.sku}`}
                                    {showStock && (
                                        <span className="ml-2">· Stock {p.stock ?? 0}</span>
                                    )}
                                </div>
                            </div>
                            <div className="font-display font-bold shrink-0">{formatPrice(p.price)}</div>
                        </button>
                    ))}
                    {total > results.length && displayQuery.trim() && (
                        <Link
                            to={`/productos?q=${encodeURIComponent(displayQuery.trim())}`}
                            onClick={() => setOpen(false)}
                            className="block p-3 text-center text-xs font-bold uppercase tracking-widest text-[var(--lz-orange)] hover:bg-[#f3f4f6]"
                        >
                            Ver todos ({total})
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
}
